import { Modal } from "@legal-500/legal500-design-system";
import { type ReactNode, type RefObject, useEffect, useId, useRef, useState } from "react";
import styles from "../PendingCompanies/Review.module.css";

type RecordTab = "details" | "history";
const RECORD_TABS: { id: RecordTab; label: string }[] = [
  { id: "details", label: "Details" },
  { id: "history", label: "Audit History" },
];

export function RecordDetailsModal({
  title,
  onClose,
  notice,
  details,
  history,
  footer,
  contentRef,
}: {
  title: string;
  onClose: () => void;
  notice?: ReactNode;
  details: ReactNode;
  history: ReactNode;
  footer?: ReactNode;
  contentRef?: RefObject<HTMLDivElement | null>;
}) {
  const [tab, setTab] = useState<RecordTab>("details");
  const idBase = useId();
  const ownContent = useRef<HTMLDivElement>(null);
  const content = contentRef ?? ownContent;
  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    content.current?.focus();
    const dialog = content.current?.closest('[role="dialog"]');
    const trap = (event: Event) => {
      const key = event as KeyboardEvent;
      if (key.key !== "Tab") return;
      const controls = [
        ...(dialog?.querySelectorAll<HTMLElement>(
          ":is(a[href], button, input, select, textarea, [tabindex]):not(:disabled)",
        ) ?? []),
      ].filter(element => element.tabIndex >= 0 && !element.closest("[hidden]"));
      if (!controls.length) return;
      const first = controls[0];
      const last = controls[controls.length - 1];
      if (
        key.shiftKey &&
        (document.activeElement === first || document.activeElement === content.current)
      ) {
        key.preventDefault();
        last.focus();
      } else if (!key.shiftKey && document.activeElement === last) {
        key.preventDefault();
        first.focus();
      }
    };
    dialog?.addEventListener("keydown", trap);
    return () => {
      dialog?.removeEventListener("keydown", trap);
      previous?.focus();
    };
  }, [content]);
  const tabId = (value: RecordTab) => `${idBase}-${value}-tab`;
  const panelId = (value: RecordTab) => `${idBase}-${value}-panel`;
  return (
    <Modal isOpen onClose={onClose} title={title} size="mediumLarge" className={styles.reviewModal}>
      <div ref={content} tabIndex={-1} className={styles.panel}>
        {notice}
        {details && (
          <>
            <div
              role="tablist"
              aria-label={`${title} sections`}
              className={styles.tabs}
              onKeyDown={event => {
                if (!["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) return;
                event.preventDefault();
                const index = RECORD_TABS.findIndex(entry => entry.id === tab);
                const next =
                  event.key === "Home"
                    ? 0
                    : event.key === "End"
                      ? RECORD_TABS.length - 1
                      : (index + (event.key === "ArrowRight" ? 1 : -1) + RECORD_TABS.length) %
                        RECORD_TABS.length;
                setTab(RECORD_TABS[next].id);
                event.currentTarget.querySelectorAll<HTMLElement>('[role="tab"]')[next]?.focus();
              }}
            >
              {RECORD_TABS.map(entry => (
                <button
                  key={entry.id}
                  type="button"
                  role="tab"
                  id={tabId(entry.id)}
                  aria-selected={tab === entry.id}
                  aria-controls={panelId(entry.id)}
                  tabIndex={tab === entry.id ? 0 : -1}
                  className={tab === entry.id ? styles.tabActive : styles.tab}
                  onClick={() => setTab(entry.id)}
                >
                  {entry.label}
                </button>
              ))}
            </div>
            <div
              role="tabpanel"
              id={panelId("details")}
              aria-labelledby={tabId("details")}
              hidden={tab !== "details"}
              className={styles.tabPanel}
            >
              {details}
            </div>
            <div
              role="tabpanel"
              id={panelId("history")}
              aria-labelledby={tabId("history")}
              hidden={tab !== "history"}
              className={styles.tabPanel}
            >
              {history}
            </div>
            {footer && <div className={styles.footer}>{footer}</div>}
          </>
        )}
      </div>
    </Modal>
  );
}
